import { useEffect, useMemo, useState } from 'react'
import {
  Control,
  useController,
  useFormContext,
  UseFormTrigger,
} from 'react-hook-form'
import { RadioButtonSelect } from 'components/FormUtils/RadioButtonSelect'
import { Building2, MapPin } from 'lucide-react'
import { SelectDoorsSection } from '../SelectLocations/SelectLocationSection'
import apiService from 'services/api'
import { z } from 'zod'

export const selectDoorsStepFormSchema = z.object({
  apply_to: z
    .union([z.literal('entire_company'), z.literal('select_locations')])
    .default('entire_company'),
  door_ids: z.array(z.number()).optional(),
  is_all_doors_selected: z.boolean().optional(),
})

interface SelectDoorsStepFormValues {
  apply_to: 'entire_company' | 'select_locations'
  door_ids?: number[]
  is_all_doors_selected?: boolean
}

export const checkDoorSelectionRules = (
  val: SelectDoorsStepFormValues,
  ctx: z.RefinementCtx
) => {
  if (val.apply_to === 'select_locations' && val.is_all_doors_selected) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'You cannot select all doors, use "Entire Company" instead',
      path: ['door_ids'],
    })
  } else if (
    val.apply_to === 'select_locations' &&
    (!val.door_ids || val.door_ids.length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'You must select at least one door',
      path: ['door_ids'],
    })
  }
}

interface SelectDoorsStepProps {
  chainProxyId: number
  initialDoorIds?: number[]
  radioGroupLabel?: string
}

export const SelectDoorsStep = ({
  chainProxyId,
  initialDoorIds,
  radioGroupLabel,
}: SelectDoorsStepProps) => {
  const { control, trigger: formTrigger } =
    useFormContext<SelectDoorsStepFormValues>()
  return (
    <SelectDoorsStepWithControl
      chainProxyId={chainProxyId}
      initialDoorIds={initialDoorIds}
      radioGroupLabel={radioGroupLabel}
      control={control}
      formTrigger={formTrigger}
    />
  )
}

interface SelectDoorsStepWithControlProps<
  T extends SelectDoorsStepFormValues,
> extends SelectDoorsStepProps {
  control: Control<T>
  formTrigger: UseFormTrigger<T>
}

export const SelectDoorsStepWithControl = <
  T extends SelectDoorsStepFormValues,
>({
  control: _control,
  chainProxyId,
  initialDoorIds,
  formTrigger: _formTrigger,
  radioGroupLabel = 'Apply this deal to:',
}: SelectDoorsStepWithControlProps<T>) => {
  const control = _control as unknown as Control<SelectDoorsStepFormValues>
  const formTrigger =
    _formTrigger as unknown as UseFormTrigger<SelectDoorsStepFormValues>

  const api = apiService()
  const {
    field: { onChange: onChangeDoorIds },
    fieldState: { error: doorIdsError },
  } = useController({ name: 'door_ids', control })

  const {
    field: { onChange: onChangeIsAllDoorsSelected },
  } = useController({ name: 'is_all_doors_selected', control })

  const [selectedCount, setSelectedCount] = useState(0)

  const {
    field: { value: applyTo },
  } = useController({ name: 'apply_to', control })

  const isSelectLocationsSelected = useMemo(
    () => applyTo === 'select_locations',
    [applyTo]
  )

  useEffect(() => {
    if (!isSelectLocationsSelected) {
      onChangeDoorIds([])
      onChangeIsAllDoorsSelected(false)
    }
  }, [isSelectLocationsSelected])

  const { data: { count: companyTotalDoors } = { count: 0 } } =
    api.useGetDoorsForSelectionCount(chainProxyId)

  useEffect(() => {
    if (companyTotalDoors) {
      if (companyTotalDoors > 0 && selectedCount >= companyTotalDoors) {
        onChangeIsAllDoorsSelected(true)
      } else {
        onChangeIsAllDoorsSelected(false)
      }
      formTrigger('door_ids')
    }
  }, [selectedCount, companyTotalDoors])
  const radioGroupOptions = useMemo(() => {
    return [
      {
        key: 'entire_company',
        icon: Building2,
        label: 'Entire Company',
      },
      {
        key: 'select_locations',
        icon: MapPin,
        label: 'Select locations',
        props: {
          disabled: companyTotalDoors === 1,
        },
        tooltip:
          companyTotalDoors === 1
            ? 'There is only one location for this company'
            : undefined,
      },
    ]
  }, [companyTotalDoors])

  return (
    <div className="w-full bg-white rounded-lg shadow-md p-4 border border-gray-200 mb-3">
      <div className="flex items-center">
        <span className="text-gray-800 text-base font-medium mr-6 min-w-fit">
          {radioGroupLabel}
        </span>
        <RadioButtonSelect
          control={control}
          name="apply_to"
          options={radioGroupOptions}
          horizontal
        />
      </div>
      {isSelectLocationsSelected && (
        <>
          {doorIdsError?.message && (
            <div className="text-red-500 mt-2">{doorIdsError.message}</div>
          )}
          <div className="mt-4">
            <SelectDoorsSection
              onSelectedIdsChange={onChangeDoorIds}
              chainProxyId={chainProxyId}
              onSelectedCountChange={setSelectedCount}
              initialDoorIds={initialDoorIds}
            />
          </div>
        </>
      )}
    </div>
  )
}
